"use client";

import * as React from "react";
import { Dialog as DialogPrimitive } from "radix-ui";

import { Button } from "@/components/ui/button";
import { getFrontDrawerSurface, useDrawerSurface } from "@/components/ui/drawerSurfaceContext";
import { headingVariantClassName } from "@/components/ui/heading";
import { shouldPreventDialogDismiss } from "@/lib/portaledPicker";
import { cn } from "@/lib/utils";
import { XIcon } from "lucide-react";

function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "fixed inset-0 isolate z-50 bg-black/10 duration-100 supports-backdrop-filter:backdrop-blur-xs data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0",
        className,
      )}
      {...props}
    />
  );
}

function preventDialogDismissOnOutsideInteraction(
  event: Event & { preventDefault: () => void },
): void {
  if (shouldPreventDialogDismiss(event.target)) {
    event.preventDefault();
  }
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  containInDrawer = true,
  onPointerDownOutside,
  onFocusOutside,
  onInteractOutside,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean;
  /**
   * When a left/right drawer is open, portal into it and center there.
   * Set `false` for app chrome and full-viewport overlays (search, photo markup).
   * Hidden drawers (display: none) are ignored.
   */
  containInDrawer?: boolean;
}) {
  const contextSurface = useDrawerSurface();
  const drawerSurface = containInDrawer ? (contextSurface ?? getFrontDrawerSurface()) : null;
  const nestedInDrawer = drawerSurface != null;

  return (
    <DialogPortal container={nestedInDrawer ? drawerSurface : undefined}>
      <DialogOverlay className={nestedInDrawer ? "absolute" : undefined} />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        data-nested-in-drawer={nestedInDrawer ? "true" : undefined}
        className={cn(
          "top-1/2 left-1/2 z-50 grid w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 gap-4 rounded-xl bg-popover p-4 text-sm text-popover-foreground ring-1 ring-foreground/10 duration-100 outline-none sm:max-w-sm data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
          nestedInDrawer ? "absolute" : "fixed",
          className,
        )}
        onPointerDownOutside={(event) => {
          preventDialogDismissOnOutsideInteraction(event);
          onPointerDownOutside?.(event);
        }}
        onFocusOutside={(event) => {
          preventDialogDismissOnOutsideInteraction(event);
          onFocusOutside?.(event);
        }}
        onInteractOutside={(event) => {
          preventDialogDismissOnOutsideInteraction(event);
          onInteractOutside?.(event);
        }}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close data-slot="dialog-close" asChild>
            <Button variant="ghost" className="absolute top-2 right-2" size="icon-sm">
              <XIcon />
              <span className="sr-only">Close</span>
            </Button>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div data-slot="dialog-header" className={cn("flex flex-col gap-2", className)} {...props} />
  );
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  showCloseButton?: boolean;
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "-mx-4 -mb-4 flex flex-row gap-2 rounded-b-xl border-t bg-muted/50 p-4 max-sm:[&_[data-slot=button]]:min-w-0 max-sm:[&_[data-slot=button]]:flex-1 sm:justify-end",
        className,
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close asChild>
          <Button variant="outline">Close</Button>
        </DialogPrimitive.Close>
      )}
    </div>
  );
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn(headingVariantClassName.drawer, "leading-none", className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        "text-sm text-muted-foreground *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground",
        className,
      )}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
